import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PaymentPage } from "@/pages/payment-pages/types/payment-page";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";

type PaymentPageCardProps = {
  paymentPages: PaymentPage[];
  setShowDrawer: (value: boolean) => void;
  setSelected: (value: PaymentPage | null) => void;
  onDelete?: (id: string) => void;
  onAdd?: () => void;
};

export default function PaymentPageCard({
  paymentPages,
  setShowDrawer,
  setSelected,
  onDelete,
  onAdd,
}: PaymentPageCardProps) {
  return (
    <div className="w-full">
      <Card className="overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-3xl">Payment Pages</CardTitle>
              <CardDescription className="text-sm">
                All payment pages
              </CardDescription>
            </div>
            <HasAccess permission="write">
              <Button className="bg-sidebar" onClick={() => onAdd?.()}>
                Add Payment Page
              </Button>
            </HasAccess>
          </div>
        </CardHeader>
        <ScrollArea className="h-[calc(100%-5rem)]">
          <CardContent className="space-y-10">
            {paymentPages.map((paymentPage) => (
              <Card
                key={paymentPage.id}
                className="overflow-hidden cursor-pointer"
                onClick={() => {
                  setSelected(paymentPage);
                  setShowDrawer(true);
                }}
              >
                <CardHeader className="bg-gray-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{paymentPage.page_title}</CardTitle>
                    <div className="flex gap-2">
                      <Badge
                        variant={
                          paymentPage.status === "ACTIVE" ? "default" : "secondary"
                        }
                      >
                        {paymentPage.status}
                      </Badge>
                      <Badge variant="outline">{paymentPage.region}</Badge>
                      {paymentPage.is_auth_enabled && (
                        <Badge variant="default">Auth</Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription className="text-sm mt-2">
                    slug: {paymentPage.slug} · page id: {paymentPage.page_id}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Fallback amount</span>
                    <span className="font-semibold text-xl">
                      {(paymentPage.fallback_amount ?? "—").toString()}{" "}
                      <span className="text-muted-foreground font-normal text-base">
                        {paymentPage.currency}
                      </span>
                    </span>
                  </div>
                  {onDelete && (
                    <HasAccess permission="write">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="w-fit"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(paymentPage.id);
                        }}
                      >
                        Delete
                      </Button>
                    </HasAccess>
                  )}
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </ScrollArea>
      </Card>
    </div>
  );
}
